import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Globe } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { language, toggleLanguage, t } = useLanguage();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { to: '/', label: t.nav.home },
    { to: '/guides', label: t.nav.guides },
    { to: '/blog', label: t.nav.blog },
  ];

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled 
            ? 'bg-[#1C1C1E]/90 backdrop-blur-xl border-b border-[#2C2C2E]' 
            : 'bg-transparent'
        }`}
        data-testid="navbar"
      >
        <div className="w-full px-1 md:px-6">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <Link 
              to="/" 
              className="flex items-center hover:opacity-70 transition-opacity -ml-2 md:ml-0"
              data-testid="logo"
            >
              <img 
                src="/logo-huacaya.png" 
                alt="Huacaya" 
                className="h-14 md:h-16 w-auto"
                style={{ filter: 'brightness(0) invert(1)' }}
              />
            </Link>

            {/* Desktop Navigation - Right aligned with language */}
            <div className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`font-['Inter'] text-base tracking-wide transition-colors ${
                    location.pathname === link.to
                      ? 'text-[#C9B896]'
                      : 'text-[#A1A1A6] hover:text-[#F5F5F7]'
                  }`}
                  data-testid={`nav-${link.label.toLowerCase()}`}
                >
                  {link.label}
                </Link>
              ))}
              
              {/* Language Toggle */}
              <button
                onClick={toggleLanguage}
                className="flex items-center gap-1.5 text-base font-medium text-[#A1A1A6] hover:text-[#F5F5F7] transition-colors ml-4"
                data-testid="language-toggle"
              >
                <Globe className="w-5 h-5" />
                <span className="uppercase">{language}</span>
              </button>
            </div>

            {/* Mobile - Language + Menu */}
            <div className="flex md:hidden items-center gap-4">
              <button
                onClick={toggleLanguage}
                className="flex items-center gap-1.5 text-base font-medium text-[#A1A1A6] hover:text-[#F5F5F7] transition-colors"
                data-testid="language-toggle-mobile"
              >
                <Globe className="w-5 h-5" />
                <span className="uppercase">{language}</span>
              </button>
              
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2 text-[#F5F5F7]"
                data-testid="mobile-menu-toggle"
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-[#1C1C1E] pt-20 md:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col items-center gap-8 pt-12">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`font-['Manrope'] text-2xl font-semibold transition-colors ${
                    location.pathname === link.to
                      ? 'text-[#C9B896]'
                      : 'text-[#F5F5F7]'
                  }`}
                  data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
